import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["collapsed", "panelType", "opaqueBackground", "children"],
  _excluded2 = ["panelInTransition", "children"],
  _excluded3 = ["collapsed"];
import { Component } from 'react';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import { CSSTransitionGroup } from 'react-transition-group';
import PropTypes from 'prop-types';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/components/View';
import styled from 'foundations-theming/utils';
import { FLOATY, PUSHY, COLLAPSED, TOGGLED_ANIMATION_DURATION, PANEL } from '../EPConstants';
import FadingVeil from './FadingVeil';
import { getPatagoniaPanelAnimationType, easeInOutSine, computeUpdatedWidth } from '../utils/animationUtils';
import { getPanelFromSidebarOptions } from '../utils/sidebarUtils';
import { filterProps } from 'foundations-components/utils/filterProps';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { Badge } from 'foundations-components/transitional/components/Badge';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEFAULT_EXPANDED_WIDTH = 567;
const getWidthForPanel = (sidebarOptions, panelName) => {
  if (!panelName) {
    return 0;
  }
  const panel = getPanelFromSidebarOptions(sidebarOptions, panelName);
  return panel ? panel.contentWidth : 0;
};
const getIsPanelEmpty = panel => panel === '';
const Sidebar = styled.div.withConfig({
  displayName: "EPSideToolbar__Sidebar"
})(({
  $collapsed,
  $panelType,
  $opaqueBackground,
  theme,
  $cssStyles
}) => Object.assign({
  position: 'relative',
  height: '100%',
  borderRight: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`,
  zIndex: '5',
  background: $collapsed || $panelType === PUSHY || $opaqueBackground ? theme.color['fill-surface-default'] : `${theme.color['fill-surface-default']}F2`,
  display: 'flex'
}, $cssStyles));
const SidebarContainer = _ref => {
  let {
      collapsed,
      panelType,
      opaqueBackground,
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  // @bend-expect trellis-tokenized
  return /*#__PURE__*/_jsx(Sidebar, Object.assign({
    $collapsed: collapsed,
    $panelType: panelType,
    $opaqueBackground: opaqueBackground,
    $cssStyles: cssStyles
  }, domProps, {
    children: children
  }));
};
const SidebarContent = styled.div.withConfig({
  displayName: "EPSideToolbar__SidebarContent"
})(({
  collapsed,
  panelType,
  showScroll,
  opaqueBackground,
  theme
}) => {
  const isCollapsedOrFloaty = panelType === COLLAPSED || panelType === FLOATY;
  const isCollapsedOrPushy = panelType === COLLAPSED || panelType === PUSHY;
  return {
    position: isCollapsedOrFloaty ? 'absolute' : 'relative',
    height: '100%',
    overflowY: showScroll ? 'auto' : 'hidden',
    overflowX: 'hidden',
    zIndex: panelType === FLOATY ? '6' : '4',
    background: isCollapsedOrPushy || opaqueBackground ? theme.color['fill-surface-default'] : `${theme.color['fill-surface-default']}F2`,
    borderLeft: !collapsed ? `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}` : 'none',
    borderRight: panelType === FLOATY ? `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}` : 'none'
  };
});
const Wrapper = styled.span.withConfig({
  displayName: "EPSideToolbar__Wrapper"
})(({
  disabled,
  collapsed,
  buttonIsActive,
  theme
}) => {
  const backgroundColor = buttonIsActive && !collapsed ? theme.color['fill-tertiary-pressed'] : theme.color['fill-surface-default'];
  return {
    backgroundColor,
    // Not ideal, but a workaround because the ButtonGroup component renders a
    // dark grey square behind disabled buttons with no way to update the border radius.
    // This hides the tiny corners of gray that poke out behind the button.
    borderRadius: disabled ? theme.space['0'] : theme.space['100'],
    padding: theme.space['50'],
    '&&&:hover': {
      backgroundColor: !disabled && !buttonIsActive ? theme.color['fill-tertiary-hover'] : ''
    }
  };
});
const BetaBadge = () => {
  return /*#__PURE__*/_jsx(View, {
    insetInlineStart: "300",
    insetBlockEnd: "800",
    position: "absolute",
    zIndex: "10",
    children: /*#__PURE__*/_jsx(Badge, {
      use: "beta",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "epSideToolbar.betaBadge"
      })
    })
  });
};
const SidebarButton = ({
  buttonIsActive,
  collapsed,
  disabled,
  iconNode,
  onClick,
  panelName,
  showBetaBadge
}) => {
  return /*#__PURE__*/_jsx(Button, {
    disabled: disabled,
    onClick: onClick,
    marginBlock: "200",
    use: "unstyled",
    children: /*#__PURE__*/_jsxs(Wrapper, {
      disabled: disabled,
      collapsed: collapsed,
      buttonIsActive: buttonIsActive,
      children: [showBetaBadge && /*#__PURE__*/_jsx(BetaBadge, {}), iconNode && /*#__PURE__*/_jsx(IconHolder, {
        color: disabled ? 'icon-core-disabled' : 'icon-core-default',
        "data-sidebar-button-id": panelName,
        "data-onboarding": `ep-side-toolbar-${panelName}`,
        "data-campaign-assistant-onboarding-tour": `tour-step-${panelName}`,
        icon: iconNode,
        padding: "100",
        size: "500"
      })]
    })
  });
};
const SidebarMenuSeparator = styled.div.withConfig({
  displayName: "EPSideToolbar__SidebarMenuSeparator"
})(({
  theme,
  $addMarginBlockEnd
}) => {
  return {
    height: theme.space['25'],
    backgroundColor: theme.color['border-core-subtle'],
    marginInline: theme.space['100'],
    marginBlockEnd: $addMarginBlockEnd ? theme.space['250'] : '0'
  };
});
const WhiteVeilAndContent = styled.div.withConfig({
  displayName: "EPSideToolbar__WhiteVeilAndContent"
})(({
  $panelInTransition,
  $cssStyles
}) => Object.assign({
  // We want to mount the child components, but not show them until sidebar
  // currently animating open or close. Rather, we want to render this just as the animation
  // finishes so it shows up just as the veil is being removed.
  visibility: $panelInTransition ? 'hidden' : 'visible',
  height: '100%'
}, $cssStyles));
const WhiteVeilAndContentContainer = _ref2 => {
  let {
      panelInTransition,
      children
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(WhiteVeilAndContent, {
      $panelInTransition: panelInTransition,
      $cssStyles: cssStyles,
      children: children
    })
  );
};
class EPSideToolbar extends Component {
  constructor(_props) {
    super(_props);
    this.handlePanelTransition = () => {
      this.setState({
        panelInTransition: true
      });
      setTimeout(() => {
        const {
          onContentShown
        } = this.props;
        this.setState({
          panelInTransition: false
        });
        if (onContentShown) {
          onContentShown();
        }
      }, TOGGLED_ANIMATION_DURATION);
    };
    this.onAnimationFrame = newWidth => {
      const {
        onToggleAnimationFrame
      } = this.props;
      if (onToggleAnimationFrame) onToggleAnimationFrame(newWidth, this.animationType);
    };
    this.customToggleAnimation = timestamp => {
      if (!this.sidebarDomNode) {
        return;
      }
      if (!this.animationFrameStart) {
        this.animationFrameStart = timestamp;
      }
      const ratioAnimationComplete = (timestamp - this.animationFrameStart) / TOGGLED_ANIMATION_DURATION;
      if (ratioAnimationComplete < 1) {
        const easingCoefficient = easeInOutSine(ratioAnimationComplete);
        const newSidebarWidth = computeUpdatedWidth(easingCoefficient, this.startSidebarWidth, this.finalSidebarWidth);
        this.sidebarDomNode.style.width = `${newSidebarWidth}px`;
        this.onAnimationFrame(newSidebarWidth);
        this.requestAnimationFrameId = requestAnimationFrame(this.customToggleAnimation);
      } else {
        this.sidebarDomNode.style.width = `${this.finalSidebarWidth}px`;
        this.onAnimationFrame(this.finalSidebarWidth);
        this.handleToggleAnimationEnd();
        this.clearAnimationParameters();
      }
    };
    this.handleToggleAnimationEnd = () => {
      const {
        onToggleAnimationEnd
      } = this.props;
      if (onToggleAnimationEnd) onToggleAnimationEnd();
    };
    this.initSidebarDomNode = () => {
      if (!this.sidebarDomNode) {
        this.sidebarDomNode = this.sidebarRef;
      }
    };
    this.setupToggleAnimationParameters = (finalWidth, previousPanelType, nextPanelType) => {
      if (!this.sidebarRef) {
        return;
      }
      const sidebarRect = this.sidebarRef.getBoundingClientRect();
      if (this.props.onToggleAnimationStart) {
        this.props.onToggleAnimationStart();
      }
      this.initSidebarDomNode();
      this.startSidebarWidth = sidebarRect.width;
      this.finalSidebarWidth = finalWidth;
      this.animationType = getPatagoniaPanelAnimationType(previousPanelType, nextPanelType);
    };
    this.setupWidthAnimationParameters = finalWidth => {
      if (this.sidebarRef) {
        const sidebarRect = this.sidebarRef.getBoundingClientRect();
        const sidebarWidth = sidebarRect.width;
        if (this.props.onWidthAnimationStart) {
          this.props.onWidthAnimationStart(sidebarWidth);
        }
        this.initSidebarDomNode();
        this.startSidebarWidth = sidebarWidth;
        this.finalSidebarWidth = finalWidth;
      }
    };
    this.clearAnimationParameters = () => {
      this.animationFrameStart = null;
      this.startSidebarWidth = 0;
      this.finalSidebarWidth = 0;
      this.requestAnimationFrameId = null;
      this.animationType = null;
    };
    this.collapseSideToolbarPanel = previousPanel => {
      this.setupToggleAnimationParameters(0, this.getPanelType(previousPanel), COLLAPSED);
      if (this.requestAnimationFrameId) {
        cancelAnimationFrame(this.requestAnimationFrameId);
      }
      this.requestAnimationFrameId = requestAnimationFrame(this.customToggleAnimation);
    };
    this.openToSideToolbarPanel = (previousPanel, nextPanel) => {
      const {
        sidebarOptions
      } = this.props;
      const nextActivePanel = getPanelFromSidebarOptions(sidebarOptions, nextPanel);
      if (nextActivePanel) {
        this.setupToggleAnimationParameters(nextActivePanel.contentWidth, this.getPanelType(previousPanel), nextActivePanel.panelDisplay.type);
        if (this.requestAnimationFrameId) {
          cancelAnimationFrame(this.requestAnimationFrameId);
        }
        this.requestAnimationFrameId = requestAnimationFrame(this.customToggleAnimation);
      }
    };
    this.renderWhiteVeilAndContent = content => {
      const {
        panelInTransition
      } = this.state;
      const props = this.props;
      const cssTransition = /*#__PURE__*/_jsx(CSSTransitionGroup, {
        transitionName: "white-veil",
        transitionAppear: true,
        transitionEnter: true,
        transitionLeave: true,
        transitionAppearTimeout: 100,
        transitionEnterTimeout: 100,
        transitionLeaveTimeout: 250,
        component: "div",
        children: panelInTransition && /*#__PURE__*/_jsx(FadingVeil, {}, 'white-veil-animation')
      });
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [cssTransition, /*#__PURE__*/_jsx(WhiteVeilAndContentContainer, Object.assign({
          panelInTransition: panelInTransition
        }, props.sidebarContentStyles, {
          children: content
        }))]
      });
    };
    this.updateActivePanel = panelName => {
      const {
        handlePanelChange
      } = this.props;
      handlePanelChange(panelName);
    };
    this.handleSidebarButton = panelName => {
      const {
        collapsed,
        onExpand,
        onCollapse,
        activePanel
      } = this.props;
      if (collapsed) {
        this.updateActivePanel(panelName);
        this.openToSideToolbarPanel('', panelName);
        onExpand === null || onExpand === void 0 || onExpand();
      } else {
        if (panelName === activePanel) {
          onCollapse === null || onCollapse === void 0 || onCollapse();
          this.collapseSideToolbarPanel(activePanel);
          this.updateActivePanel('');
        } else {
          this.openToSideToolbarPanel(activePanel, panelName);
          this.updateActivePanel(panelName);
        }
      }
    };
    this.renderTrellisIcons = () => {
      const {
        sidebarOptions,
        collapsed,
        activePanel
      } = this.props;
      return sidebarOptions.map((option, index) => {
        if (option.type === PANEL) {
          if (option.hideItem || !('iconNode' in option)) {
            return null;
          }
          const buttonIsActive = option.panelName === activePanel;
          const disabled = Boolean(option.disabled);
          return /*#__PURE__*/_jsx(Tooltip, {
            placement: "right",
            title: option.tooltipLabel,
            disabled: !option.tooltipLabel,
            children: /*#__PURE__*/_jsx(SidebarButton, {
              buttonIsActive: buttonIsActive,
              collapsed: collapsed,
              disabled: disabled,
              iconNode: option.iconNode,
              onClick: () => this.handleSidebarButton(option.panelName),
              panelName: option.panelName,
              showBetaBadge: option.showBetaBadge
            })
          }, `epSideToolbarIcon-${index}`);
        } else {
          return /*#__PURE__*/_jsx(SidebarMenuSeparator, {
            $addMarginBlockEnd: false
          }, index);
        }
      });
    };
    this.panelToRender = () => {
      const {
        sidebarOptions,
        activePanel
      } = this.props;
      const DEFAULT_PANEL = {
        content: '',
        contentWidth: 0,
        panelDisplay: {
          type: COLLAPSED,
          showScroll: false,
          opaqueBackground: false
        }
      };
      const panelToRender = getPanelFromSidebarOptions(sidebarOptions, activePanel);
      return panelToRender || DEFAULT_PANEL;
    };
    this.renderSidebarContent = () => {
      const {
        activePanel
      } = this.props;
      if (!activePanel) {
        return null;
      }
      return this.renderWhiteVeilAndContent(this.panelToRender().content);
    };
    this.state = {
      panelInTransition: false
    };
    this.sidebarDomNode = null;
    this.animationFrameStart = null;
    this.startSidebarWidth = 0;
    this.finalSidebarWidth = 0;
    this.requestAnimationFrameId = null;
    this.animationType = null;
    this.sidebarRef = null;
    this.customWidthAnimation = this.customWidthAnimation.bind(this);
  }
  componentDidMount() {
    this.initSidebarDomNode();
    if (!this.sidebarDomNode) {
      return;
    }
    const sidebarRefInlineWidth = this.sidebarDomNode.style.width;
    const currentPanel = this.panelToRender();
    const currentExpectedWidth = currentPanel.contentWidth;

    // Ensure our inline styles are set for the first time the sidebar is toggled
    if (sidebarRefInlineWidth === '') {
      // Use requestAnimationFrame to make sure we aren't triggering any reflows
      window.requestAnimationFrame(() => {
        if (this.sidebarDomNode) {
          this.sidebarDomNode.style.width = `${currentExpectedWidth}px`;
        }
      });
    }
  }
  componentDidUpdate(prevProps) {
    const {
      activePanel: currentActivePanel,
      sidebarOptions
    } = this.props;
    const previousActivePanel = prevProps.activePanel;
    const currentPanelIsEmpty = getIsPanelEmpty(currentActivePanel);
    const activePanelHasChanged = previousActivePanel !== currentActivePanel;
    if (activePanelHasChanged) {
      this.handlePanelTransition();
      if (currentPanelIsEmpty) {
        this.collapseSideToolbarPanel(previousActivePanel);
      } else {
        this.openToSideToolbarPanel(previousActivePanel, currentActivePanel);
      }
    } else if (!currentPanelIsEmpty && getWidthForPanel(sidebarOptions, currentActivePanel) !== getWidthForPanel(prevProps.sidebarOptions, previousActivePanel)) {
      this.animateActivePanelWidth(getWidthForPanel(sidebarOptions, currentActivePanel));
    }
  }
  getPanelType(panelName) {
    const {
      sidebarOptions
    } = this.props;
    const panel = getPanelFromSidebarOptions(sidebarOptions, panelName);
    return panel ? panel.panelDisplay.type : COLLAPSED;
  }
  handleWidthAnimationEnd() {
    const {
      onWidthAnimationEnd
    } = this.props;
    if (onWidthAnimationEnd) onWidthAnimationEnd();
  }
  onWidthAnimationFrame(easingCoefficient) {
    const {
      onWidthAnimationFrame
    } = this.props;
    if (onWidthAnimationFrame) onWidthAnimationFrame(easingCoefficient);
  }
  customWidthAnimation(timestamp) {
    if (!this.sidebarDomNode) {
      return;
    }
    if (!this.animationFrameStart) {
      this.animationFrameStart = timestamp;
    }
    const ratioAnimationComplete = (timestamp - this.animationFrameStart) / TOGGLED_ANIMATION_DURATION;
    if (ratioAnimationComplete < 1) {
      const easingCoefficient = easeInOutSine(ratioAnimationComplete);
      const newSidebarWidth = computeUpdatedWidth(easingCoefficient, this.startSidebarWidth, this.finalSidebarWidth);
      this.sidebarDomNode.style.width = `${newSidebarWidth}px`;
      this.onWidthAnimationFrame(easingCoefficient);
      this.requestAnimationFrameId = requestAnimationFrame(this.customWidthAnimation);
    } else {
      this.sidebarDomNode.style.width = `${this.finalSidebarWidth}px`;
      this.onWidthAnimationFrame(null);
      this.handleWidthAnimationEnd();
      this.clearAnimationParameters();
    }
  }
  animateActivePanelWidth(finalWidth) {
    this.setupWidthAnimationParameters(finalWidth);
    if (this.requestAnimationFrameId) {
      cancelAnimationFrame(this.requestAnimationFrameId);
    }
    this.requestAnimationFrameId = requestAnimationFrame(this.customWidthAnimation);
  }

  //sidebarOptions should be passed as for example see builder-demo-app's HomeContainer

  render() {
    const _this$props = this.props,
      {
        collapsed
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded3);
    const panelToRender = this.panelToRender();
    const panelType = panelToRender.panelDisplay.type;
    const expandedWidth = panelToRender.contentWidth;
    const showScroll = panelToRender.panelDisplay.showScroll;
    const opaqueBackground = panelToRender.panelDisplay.opaqueBackground;
    return /*#__PURE__*/_jsxs(SidebarContainer, Object.assign({
      collapsed: collapsed,
      "data-test-id": "ep-sidetoolbar",
      panelType: panelType,
      opaqueBackground: opaqueBackground
    }, rest, {
      children: [/*#__PURE__*/_jsx(View, {
        "data-test-id": "ep-sidetoolbar-nav-items-container",
        paddingInline: "50",
        children: /*#__PURE__*/_jsx(ButtonGroup, {
          "data-test-id": "ep-sidetoolbar-nav-items",
          gap: "250",
          marginInline: "100",
          paddingInline: "25",
          orientation: "vertical",
          children: this.renderTrellisIcons()
        })
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(SidebarContent, {
          ref: ref => {
            this.sidebarRef = ref;
          },
          panelType: panelType,
          showScroll: showScroll,
          opaqueBackground: opaqueBackground,
          collapsed: collapsed,
          "data-test-id": "ep-sidetoolbar-content",
          "data-onboarding": "ep-sidetoolbar-content",
          "data-campaign-assistant-onboarding-tour": "tour-step-toolbar-content",
          "data-test-width": expandedWidth,
          children: this.renderSidebarContent()
        })
      })]
    }));
  }
}
EPSideToolbar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  onExpand: PropTypes.func.isRequired,
  onCollapse: PropTypes.func.isRequired,
  sidebarOptions: PropTypes.array.isRequired,
  handlePanelChange: PropTypes.func.isRequired,
  activePanel: PropTypes.string.isRequired,
  onContentShown: PropTypes.func,
  onToggleAnimationFrame: PropTypes.func,
  onToggleAnimationEnd: PropTypes.func,
  onWidthAnimationEnd: PropTypes.func,
  onToggleAnimationStart: PropTypes.func,
  onWidthAnimationStart: PropTypes.func,
  onWidthAnimationFrame: PropTypes.func
};
EPSideToolbar.defaultProps = {
  expandedWidth: DEFAULT_EXPANDED_WIDTH
};
export const TrellisEPSideToolbar = EPSideToolbar;
export default EPSideToolbar;